import { useCallback, useEffect } from "react";

import {
  useNavigate,
  useLocation,
  unstable_useBlocker as useBlocker,
} from "react-router-dom";
import type { Blocker } from "@remix-run/router";

interface UseConfirmLoseUnsavedOptions {
  /** Whether to block navigation */
  when: boolean;
  /** Message to display in confirmation dialogs */
  message?: string;
}

/**
 * A hook that prompts the user with a confirmation message before navigating away from the current page. It handles both React Router navigation (NavLinks, programmatic) and browser navigation (refresh, close, back/forward), but //!NOTE: this hook does not block modal closing
 *
 * @param {Object} options - Hook options
 * @param {boolean} options.when - Whether to block navigation (typically set to true when there are unsaved changes)
 * @param {string} [options.message] - Message to display in confirmation dialogs
 *
 * @example
 * // Basic usage
 * import { useConfirmLoseUnsaved } from "../hooks/useConfirmLoseUnsaved";
 *
 * export const MyForm = ({ isDirty }) => {
 *
 *   useConfirmLoseUnsaved({
 *     when: isDirty,
 *     // message is optional
 *   });
 *
 *   // ... form logic responsible for setting isDirty to true on change
 *   return <form>...</form>;
 * };
 */
export const useConfirmLoseUnsaved = ({
  when,
  message = "You have unsaved changes. Are you sure you want to discard these changes and leave?",
}: UseConfirmLoseUnsavedOptions): void => {
  const navigate = useNavigate();
  const location = useLocation();

  // Block React Router navigation
  const blocker: Blocker = useBlocker(when);

  // Handle the blocker state
  useEffect(() => {
    if (blocker.state === "blocked") {
      const shouldProceed: boolean = window.confirm(message);
      if (shouldProceed) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker, message]);

  // Browser navigation helpers
  const block = useCallback(
    (): boolean => (when ? window.confirm(message) : true),
    [when, message],
  );

  const handlePopState = useCallback(
    (e: PopStateEvent): void => {
      if (!block()) {
        e.preventDefault();
        navigate(location, { replace: true });
      }
    },
    [block, navigate, location],
  );

  const handleBeforeUnload = useCallback(
    (e: BeforeUnloadEvent): void => {
      if (block()) return;
      e.preventDefault();
      e.returnValue = message;
    },
    [block, message],
  );

  // Handle browser navigation (refresh, close, back/forward)
  useEffect((): (() => void) => {
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("beforeunload", handleBeforeUnload);
    return (): void => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [handlePopState, handleBeforeUnload]);
};
